import React, { useCallback, useEffect, useState } from "react";

import {
  AppShell,
  NavBar,
  Footer,
  Modal,
} from "@relentless/rmds/v1/relentless.esm.js";
import "./App.scss";
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import SocialPage from "./pages/SocialPage";
import StorePage from "./pages/StorePage";
import ContactForm from "./pages/ContactPage";
import AdminSyncPage from "./pages/AdminSyncPage";
import NotFoundPage from "./pages/NotFoundPage";
import { MOTION } from "./lib/animations";
import { initAnalytics, trackPageView } from "./lib/analytics";
import { withPublicUrl } from "./lib/publicUrl";
import { applyTheme, getInitialTheme } from "./theme";

const getSocialIconSrc = (name) =>
  withPublicUrl(`/assets/icons/${String(name || "").toLowerCase()}.svg`);

const ROUTES = [
  "home",
  "blog",
  "social",
  "store",
  "about",
  "contact",
  "admin-sync",
];

const ROUTE_TITLE_MAP = {
  home: "Home",
  about: "About",
  blog: "Blog",
  social: "Content",
  store: "Store",
  contact: "Contact",
  "admin-sync": "Admin Sync",
  "not-found": "Page Not Found",
};

export default function App() {
  const navCollapsed = false;
  const parsePath = useCallback(() => {
    const pathname =
      (typeof window !== "undefined" && window.location.pathname) || "/";
    const parts = pathname.replace(/^\//, "").split("/").filter(Boolean);
    const name = parts[0] || "home";
    const slug = parts.slice(1).join("/") || null;
    const safe = ROUTES.includes(name) ? name : "not-found";
    return {
      name: safe,
      slug: safe === name ? slug : null,
    };
  }, []);

  const [{ name: route, slug }, setRouteState] = useState(parsePath);
  const [theme, setTheme] = useState(getInitialTheme);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false,
  );

  const navigate = useCallback(
    (path) => {
      window.history.pushState({}, "", path);
      setRouteState(parsePath());
    },
    [parsePath],
  );

  useEffect(() => {
    const onPop = () => setRouteState(parsePath());
    window.addEventListener("popstate", onPop);
    onPop();
    return () => window.removeEventListener("popstate", onPop);
  }, [parsePath]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const updateMobile = () => setIsMobile(mediaQuery.matches);

    updateMobile();
    mediaQuery.addEventListener("change", updateMobile);
    return () => mediaQuery.removeEventListener("change", updateMobile);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return undefined;
    }

    const updateHeaderDock = () => {
      const header = document.querySelector(".rm-shell-header");
      if (!header) return;
      header.classList.toggle("is-docked-top", window.scrollY <= 0);
    };

    updateHeaderDock();
    window.addEventListener("scroll", updateHeaderDock, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateHeaderDock);
    };
  }, [route]);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const sectionTitle = slug
      ? "Blog Post"
      : ROUTE_TITLE_MAP[route] || ROUTE_TITLE_MAP.home;
    document.title = slug
      ? `${sectionTitle} | Relentless Website`
      : `${sectionTitle} | Relentless Website`;
    trackPageView({ pageTitle: document.title });
  }, [route, slug]);

  const items = [
    { id: "home", label: "Home", icon: "home" },
    { id: "about", label: "About", icon: "person" },
    { id: "blog", label: "Blog", icon: "article" },
    { id: "social", label: "Content", icon: "collections" },
  ];
  const navItems = items.map((item) => ({
    ...item,
    showLabels: !navCollapsed,
  }));

  const onNavigate = (id) => {
    navigate(`/${id}`);
  };

  const toggleTheme = (checked) => {
    setTheme(checked ? "dark" : "light");
  };

  const footerLinks = items
    .map((item) => ({
      id: item.id,
      label: item.label,
      href: `/${item.id}`,
      active: route === item.id,
    }))
    .concat([
      {
        id: "terms",
        label: "Terms",
        href: "/terms.html",
        active: false,
      },
      {
        id: "privacy",
        label: "Privacy",
        href: "/privacy.html",
        active: false,
      },
    ]);

  const [contactOpen, setContactOpen] = useState(false);
  const navActions = [
    {
      id: "contact",
      componentType: "Button",
      iconOnly: true,
      icon: "mail",
      onClick: () => setContactOpen(true),
    },

    {
      id: "theme-toggle",
      componentType: "toggle",
      label: "Theme",
      toggleId: "site-theme-toggle",
      className: "rm-theme-toggle",
      defaultChecked: theme === "dark",
      icon: theme === "dark" ? "dark_mode" : "light_mode",
      onClick: toggleTheme,
    },
  ];

  const routeKey = `${route}:${slug || ""}`;
  const isNotFound = route === "not-found";

  return (
    <AppShell
      header={
        isNotFound ? null : (
          <div className={navCollapsed ? "collapsed" : ""}>
            <NavBar
              items={navItems}
              activeId={route}
              onNavigate={onNavigate}
              mobile={isMobile}
              actions={navActions}
              companyName="Relentless"
            />
          </div>
        )
      }
      footer={
        isNotFound ? null : (
          <Footer
            links={footerLinks}
            email="info@therelentlessminds.com"
            logo={{
              icon: {
                src: withPublicUrl("/assets/img/logo_icon.svg"),
                asMask: true,
              },
              wordmark: {
                src: withPublicUrl("/assets/img/logo_type.svg"),
                asMask: true,
              },
            }}
            social={[
              {
                name: "linkedin",
                src: getSocialIconSrc("linkedin"),
                href: "https://www.linkedin.com/company/106010588/",
                label: "LinkedIn",
              },
              {
                name: "facebook",
                src: getSocialIconSrc("facebook"),
                href: "https://www.facebook.com/therelentlessminds",
                label: "Facebook",
              },
              {
                name: "instagram",
                src: getSocialIconSrc("instagram"),
                href: "https://www.instagram.com/therelentlessminds",
                label: "Instagram",
              },
              {
                name: "youtube",
                src: getSocialIconSrc("youtube"),
                href: "https://www.youtube.com/@therelentlessminds",
                label: "YouTube",
              },
              {
                name: "tiktok",
                src: getSocialIconSrc("tiktok"),
                href: "https://www.tiktok.com/@therelentlessminds",
                label: "TikTok",
              },
            ]}
          />
        )
      }
    >
      <div className="rm-page">
        <div
          key={routeKey}
          className="rm-route-enter"
          data-motion={MOTION.pageEnter}
        >
          {route === "home" && <HomePage />}
          {route === "about" && <AboutPage />}
          {route === "blog" &&
            (slug ? <BlogPostPage postId={slug} /> : <BlogPage />)}
          {route === "social" && <SocialPage />}
          {route === "store" && <StorePage />}
          {route === "contact" && <ContactForm />}
          {route === "admin-sync" && <AdminSyncPage />}
          {route === "not-found" && <NotFoundPage />}
        </div>
      </div>
      <Modal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Contact Us"
        variant="text"
      >
        <ContactForm />
      </Modal>
    </AppShell>
  );
}
